import { useEffect, useRef, useState } from "react";
import MarcaModeloPicker from "@/components/MarcaModeloPicker";

export type ModeloItem = { marca: string; modelo: string };

type Fila = { key: number; marca: string; modelo: string };

/**
 * Envuelve MarcaModeloPicker en una lista con soporte para varios modelos.
 *
 * - Por defecto arranca con una sola fila (UX idéntica a antes).
 * - "+ Agregar modelo" agrega filas adicionales.
 * - Botón ✕ por fila para quitar (oculto si solo queda una).
 * - El PRIMER elemento es el modelo primario del caso.
 *
 * Cada fila lleva una `key` estable (no el índice del array) para que al
 * quitar una fila intermedia React NO reuse la instancia de MarcaModeloPicker
 * con el estado de otra fila (catálogo cargado / modo "crear nuevo").
 */
function aFilas(value: ModeloItem[], desde: number): { filas: Fila[]; sig: number } {
  const base = value.length ? value : [{ marca: "", modelo: "" }];
  const filas = base.map((it, i) => ({ key: desde + i, marca: it.marca, modelo: it.modelo }));
  return { filas, sig: desde + base.length };
}

export default function MarcaModeloMultiPicker({
  value,
  onChange,
}: {
  value: ModeloItem[];
  onChange: (items: ModeloItem[]) => void;
}) {
  const nextKey = useRef(0);
  const [filas, setFilas] = useState<Fila[]>(() => {
    const r = aFilas(value, 0);
    nextKey.current = r.sig;
    return r.filas;
  });
  // Última lista que ESTE componente emitió, para distinguir un cambio
  // externo (prefill / reset desde el padre) de un eco de nuestro onChange.
  const ultimoEmitido = useRef<ModeloItem[]>(
    filas.map((f) => ({ marca: f.marca, modelo: f.modelo })),
  );

  // Si el padre reemplaza `value` por algo distinto a lo último que emitimos
  // (prefill al abrir, reset al cerrar), reconstruimos las filas con keys nuevas.
  useEffect(() => {
    const v = value.length ? value : [{ marca: "", modelo: "" }];
    const u = ultimoEmitido.current;
    const igual =
      v.length === u.length &&
      v.every((it, i) => it.marca === u[i].marca && it.modelo === u[i].modelo);
    if (!igual) {
      const r = aFilas(value, nextKey.current);
      nextKey.current = r.sig;
      setFilas(r.filas);
      ultimoEmitido.current = r.filas.map((f) => ({ marca: f.marca, modelo: f.modelo }));
    }
  }, [value]);

  function emitir(nuevas: Fila[]) {
    setFilas(nuevas);
    const items = nuevas.map((f) => ({ marca: f.marca, modelo: f.modelo }));
    ultimoEmitido.current = items;
    onChange(items);
  }

  function setItem(key: number, marca: string, modelo: string) {
    emitir(filas.map((f) => (f.key === key ? { ...f, marca, modelo } : f)));
  }

  function agregar() {
    emitir([...filas, { key: nextKey.current++, marca: "", modelo: "" }]);
  }

  function quitar(key: number) {
    if (filas.length <= 1) return;
    emitir(filas.filter((f) => f.key !== key));
  }

  return (
    <div className="space-y-2">
      {filas.map((f, i) => (
        <div key={f.key} className="flex items-start gap-2">
          {filas.length > 1 && (
            <span className="text-[10px] text-zinc-500 mt-3 w-16 shrink-0 text-right">
              {i === 0 ? "Principal" : `Modelo ${i + 1}`}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <MarcaModeloPicker
              marca={f.marca}
              modelo={f.modelo}
              onChange={(m, mo) => setItem(f.key, m, mo)}
            />
          </div>
          {filas.length > 1 && (
            <button
              type="button"
              onClick={() => quitar(f.key)}
              className="btn-ghost text-xs mt-2 shrink-0"
              aria-label={`Quitar el modelo ${f.modelo || i + 1}`}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      <button type="button" onClick={agregar} className="btn-ghost text-xs">
        + Agregar modelo
      </button>
    </div>
  );
}
